import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MdAdd,
  MdArchive,
  MdBookmarkBorder,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdCloudUpload,
  MdContentCopy,
  MdDeleteOutline,
  MdPhotoCamera,
  MdPrint,
  MdPublic,
  MdSettings,
  MdSwapHoriz,
  MdUnarchive,
  MdViewModule,
} from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import TransferStudentModal from './TransferStudentModal';
import ExternalTransferModal from './ExternalTransferModal';
import OdooAutocomplete from './OdooAutocomplete';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { getInitials } from '../../utils/attendanceFormatters';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const EMPTY_FORM = {
  first_name: '',
  middle_name: '',
  last_name: '',
  mother_name: '',
  gender: '',
  birth_date: '',
  birth_place: '',
  nationality_id: '',
  phone: '',
  mobile: '',
  email: '',
  blood_group: '',
  national_id: '',
  hemis_number: '',
  tuition_discount_percentage: '0',
  school_id: '',
  roll_no: '',
  certificate_number: '',
  graduation_year: '',
  average: '',
  admission_intake_id: '',
  district: '',
  mode: '',
  campus: '',
  faculty_id: '',
  department_id: '',
  batch_id: '',
  category: 'undergraduate',
  parent_type: '',
  parent_name: '',
  parent_phone: '',
  parent_email: '',
  image: null,
};

const STATUS_STEPS = [
  { value: 'draft', label: 'Registration' },
  { value: 'admitted', label: 'Admitted' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PARENT_TYPES = [
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'other', label: 'Other' },
];

const BLOOD_GROUPS = ['A+ve', 'A-ve', 'B+ve', 'B-ve', 'AB+ve', 'AB-ve', 'O+ve', 'O-ve'];

/** Odoo stores A+/O+; portal select uses A+ve/O+ve labels */
const BLOOD_ODOO_TO_UI = {
  'A+': 'A+ve',
  'A-': 'A-ve',
  'B+': 'B+ve',
  'B-': 'B-ve',
  'AB+': 'AB+ve',
  'AB-': 'AB-ve',
  'O+': 'O+ve',
  'O-': 'O-ve',
};
const BLOOD_UI_TO_ODOO = Object.fromEntries(
  Object.entries(BLOOD_ODOO_TO_UI).map(([odoo, ui]) => [ui, odoo])
);

const CAMPUS_FALLBACK = [
  { value: 'prof_addow', label: 'Prof Addow Campus' },
  { value: 'prof_shahiid', label: 'Prof Shahiid Campus' },
  { value: 'prof_shurie', label: 'Prof Shurie Campus' },
];

const DISTRICT_FALLBACK = [
  'Abdiaziz', 'Balcad', 'Bondhere', 'Daarusalaam', 'Daynile', 'Dharkenleey',
  'Garasbaaley', 'Gubadleey', 'Hamar-Jajab', 'Hamar-Weyne', 'Heliwa', 'Hodan',
  'Howl-Wadag', 'Karan', 'Kahda', 'Shangani', 'Shibis', 'Waberi', 'Wadajir',
  'Wardhigley', 'Yaqshid', 'Afgoye', 'Celasha',
].map((name) => ({ value: name, label: name }));

function districtValue(raw, options = []) {
  const t = firstText(raw).trim();
  if (!t) return '';
  const list = options.length ? options : DISTRICT_FALLBACK;
  const match = list.find((opt) => {
    const value = String(opt.value || opt.id || '');
    const label = String(opt.label || opt.name || '');
    return value === t || label === t || value.toLowerCase() === t.toLowerCase()
      || label.toLowerCase() === t.toLowerCase();
  });
  return match ? String(match.value || match.id || match.label) : t;
}

function odooId(value) {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) return String(value[0] ?? '');
  if (typeof value === 'object' && value.id != null) return String(value.id);
  return String(value);
}

function odooText(value) {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) return String(value[1] ?? value[0] ?? '');
  if (typeof value === 'object' && value.name != null) return String(value.name);
  return String(value);
}

function firstText(...values) {
  for (const v of values) {
    const t = odooText(v);
    if (t) return t;
  }
  return '';
}

function firstId(...values) {
  for (const v of values) {
    const id = odooId(v);
    if (id && id !== 'false' && id !== 'undefined') return id;
  }
  return '';
}

function normalizeBloodGroup(raw) {
  const t = firstText(raw).trim();
  if (!t) return '';
  if (BLOOD_GROUPS.includes(t)) return t;
  if (BLOOD_ODOO_TO_UI[t]) return BLOOD_ODOO_TO_UI[t];
  const compact = t.replace(/\s+/g, '');
  if (BLOOD_ODOO_TO_UI[compact]) return BLOOD_ODOO_TO_UI[compact];
  const asVe = compact.replace(/ve$/i, '');
  if (BLOOD_GROUPS.includes(`${asVe}ve`)) return `${asVe}ve`;
  return t;
}

function bloodGroupForApi(uiValue) {
  const t = String(uiValue || '').trim();
  if (!t) return '';
  return BLOOD_UI_TO_ODOO[t] || t;
}

/** Split "A B C" into first / middle / last when API only returns name */
function splitFullName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { first_name: '', middle_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], middle_name: '', last_name: '' };
  if (parts.length === 2) return { first_name: parts[0], middle_name: '', last_name: parts[1] };
  return {
    first_name: parts[0],
    middle_name: parts.slice(1, -1).join(' '),
    last_name: parts[parts.length - 1],
  };
}

function normalizeCategory(raw) {
  const t = odooText(raw).toLowerCase().trim();
  if (!t) return 'undergraduate';
  // Check undergraduate BEFORE "graduate" — "undergraduate" contains "graduate"
  if (t.includes('under') || t === 'ug' || t === 'undergraduate') return 'undergraduate';
  if (
    t.includes('post') ||
    t === 'pg' ||
    t.includes('master') ||
    t === 'graduate' ||
    t === 'postgraduate' ||
    t.includes('post-graduate') ||
    t.includes('post graduate')
  ) {
    return 'postgraduate';
  }
  return 'undergraduate';
}

function upsertOption(list, id, name) {
  if (!id) return list;
  const sid = String(id);
  const existing = list.find((x) => String(x.id) === sid || String(x.value) === sid);
  if (existing) {
    if (name && !existing.name) {
      return list.map((x) =>
        String(x.id) === sid || String(x.value) === sid ? { ...x, name } : x
      );
    }
    return list;
  }
  return [...list, { id: Number.isNaN(Number(sid)) ? sid : Number(sid), name: name || `#${sid}` }];
}

/** Replace list but keep currently selected row(s) so async lookups cannot wipe form values */
function mergeLookupList(incoming, selectedId, selectedName) {
  const base = Array.isArray(incoming) ? incoming : [];
  return upsertOption(base, selectedId, selectedName);
}

function Field({ label, children, required, help }) {
  return (
    <div className="ea-odoo-field">
      <label className="ea-odoo-field__label">
        {label}
        {required ? <span className="ea-adm-req">*</span> : null}
        {help ? (
          <span className="ea-odoo-help" title={help}>
            i
          </span>
        ) : null}
      </label>
      <div className="ea-odoo-field__value">{children}</div>
    </div>
  );
}

function RadioRow({ name, value, onChange, options, required }) {
  return (
    <div className="ea-adm-radios" role="radiogroup" aria-required={required || undefined}>
      {options.map((opt, idx) => (
        <label key={opt.value} className="ea-adm-radio">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            required={required && idx === 0 && !value}
          />
          <span className="ea-adm-radio__label">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function mergeById(lists) {
  const map = new Map();
  lists.flat().forEach((item) => {
    if (!item) return;
    const id = item.id ?? item.value;
    if (id == null) return;
    const name = item.name || item.label || String(id);
    if (!map.has(String(id))) map.set(String(id), { id, name });
  });
  return [...map.values()].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
  );
}

function Group({ title, children, wide }) {
  return (
    <section className={`ea-odoo-group${wide ? ' ea-odoo-group--wide' : ''}`}>
      <h3 className="ea-odoo-group__title">{title}</h3>
      <div className="ea-odoo-group__body">{children}</div>
    </section>
  );
}

export default function AdmissionsStudentFormPage() {
  const { studentId } = useParams();
  const isNew = !studentId || studentId === 'new';
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user } = useEmployeeAttendanceAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [meta, setMeta] = useState({
    admission_number: '',
    student_no: '',
    state: '',
    state_label: '',
    active: true,
    department_name: '',
    batch_name: '',
    image_url: null,
    can_admit: false,
    can_generate_student_no: false,
  });
  const [tab, setTab] = useState('family');
  const [campuses, setCampuses] = useState(CAMPUS_FALLBACK);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolCreating, setSchoolCreating] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [districts, setDistricts] = useState(DISTRICT_FALLBACK);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [externalTransferOpen, setExternalTransferOpen] = useState(false);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const actionsRef = useRef(null);

  useEffect(() => {
    if (!actionMessage) return undefined;
    const timer = setTimeout(() => setActionMessage(null), 2000);
    return () => clearTimeout(timer);
  }, [actionMessage]);

  const setField = (key, value) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fullName = useMemo(
    () => [form.first_name, form.middle_name, form.last_name].filter(Boolean).join(' '),
    [form.first_name, form.middle_name, form.last_name]
  );

  const titleName = isNew ? 'New' : fullName || meta.admission_number || 'Student';
  const showDirtyActions = isNew || dirty;
  const isArchived = meta.active === false;

  useEffect(() => {
    if (!actionsOpen) {
      setPrintOpen(false);
      return undefined;
    }
    const onDoc = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setActionsOpen(false);
        setPrintOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [actionsOpen]);

  const photoSrc =
    photoPreview ||
    (meta.image_url && String(meta.image_url).startsWith('data:')
      ? meta.image_url
      : meta.image_url
        ? `${String(admissionsAPI.baseURL || '').replace(/\/$/, '')}${
            meta.image_url.startsWith('/') ? meta.image_url : `/${meta.image_url}`
          }`
        : null);

  const applyNationalities = useCallback((...chunks) => {
    setNationalities((prev) => mergeById([prev, ...chunks]));
  }, []);

  const searchNationalities = useCallback(
    async (q) => {
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getNationalities(q || '', token, { limit: 500 })
      );
      if (!data) return;
      applyNationalities(data.nationalities || data.records || data.countries || []);
    },
    [ensureHrToken, applyNationalities]
  );

  const selectedOptionsRef = useRef({
    school_id: '',
    school_name: '',
    faculty_id: '',
    faculty_name: '',
    department_id: '',
    department_name: '',
    batch_id: '',
    batch_name: '',
    admission_intake_id: '',
    admission_intake_name: '',
    nationality_id: '',
    nationality_name: '',
  });

  const searchSchools = useCallback(
    async (query) => {
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getSchools(query || '', token, { limit: 100 })
      );
      if (!data) return;
      const matches = data.schools || data.records || [];
      setSchools((prev) => mergeById([prev, matches]));
    },
    [ensureHrToken]
  );

  const createSchool = async (name) => {
    const schoolName = String(name || '').trim();
    if (!schoolName || schoolCreating) return;
    setSchoolCreating(true);
    setError(null);
    const { data, error: createError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.createSchool(schoolName, token)
    );
    setSchoolCreating(false);
    if (createError === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (createError || !data) {
      setError(createError?.message || createError || 'Unable to create the school.');
      return;
    }
    const school = data.school || data.record;
    if (!school?.id) {
      setError('The school was created but no school ID was returned.');
      return;
    }
    setSchools((prev) => upsertOption(prev, school.id, school.name || schoolName));
    setField('school_id', String(school.id));
    setActionMessage(data.created === false ? 'Existing school selected.' : 'School created and selected.');
  };

  const loadLookups = useCallback(async () => {
    const jobs = [
      ['campuses', (t) => admissionsAPI.getCampuses(t)],
      ['faculties', (t) => admissionsAPI.getFaculties(t)],
      ['intakes', (t) => admissionsAPI.getIntakes(t)],
      ['schools', (t) => admissionsAPI.getSchools('', t, { limit: 500 })],
      ['districts', (t) => admissionsAPI.getDistricts(t)],
    ];
    for (const [key, fn] of jobs) {
      const { data } = await fetchWithHrToken(ensureHrToken, fn);
      if (!data) continue;
      const sel = selectedOptionsRef.current;
      if (key === 'campuses') {
        const list = data.campuses || data.records || [];
        if (list.length) setCampuses(list);
      }
      if (key === 'faculties') {
        setFaculties(
          mergeLookupList(
            data.faculties || data.records || [],
            sel.faculty_id,
            sel.faculty_name
          )
        );
      }
      if (key === 'intakes') {
        setIntakes(
          mergeLookupList(
            data.intakes || data.records || [],
            sel.admission_intake_id,
            sel.admission_intake_name
          )
        );
      }
      if (key === 'schools') {
        setSchools(
          mergeLookupList(
            data.schools || data.records || [],
            sel.school_id,
            sel.school_name
          )
        );
      }
      if (key === 'districts') {
        const list = data.districts || data.records || [];
        setDistricts(list.length ? list : DISTRICT_FALLBACK);
      }
    }

    // Load full country list (backend often paginates; merge multiple pulls)
    const natQueries = ['', 'Somalia', 'a', 'm', 's', 'u'];
    const chunks = [];
    for (const q of natQueries) {
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getNationalities(q, token, { limit: 500 })
      );
      if (data) {
        chunks.push(data.nationalities || data.records || data.countries || []);
      }
    }
    applyNationalities(...chunks);
    const sel = selectedOptionsRef.current;
    if (sel.nationality_id) {
      applyNationalities([{ id: Number(sel.nationality_id) || sel.nationality_id, name: sel.nationality_name }]);
    }
  }, [ensureHrToken, applyNationalities]);

  const loadDepartments = useCallback(
    async (facultyId) => {
      if (!facultyId) {
        setDepartments([]);
        return;
      }
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getDepartments(facultyId, token)
      );
      const sel = selectedOptionsRef.current;
      setDepartments(
        mergeLookupList(
          data?.departments || data?.records || [],
          sel.department_id,
          sel.department_name
        )
      );
    },
    [ensureHrToken]
  );

  const loadBatches = useCallback(
    async (departmentId, facultyId) => {
      if (!departmentId && !facultyId) {
        setBatches([]);
        return;
      }
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getBatches(departmentId, token, {
          faculty_id: facultyId || undefined,
        })
      );
      if (fetchError) {
        console.warn('Failed to load batches:', fetchError);
        return;
      }
      const sel = selectedOptionsRef.current;
      const list = data?.batches || data?.records || [];
      setBatches(mergeLookupList(list, sel.batch_id, sel.batch_name));
    },
    [ensureHrToken]
  );

  const mapStudentToForm = (s) => {
    const split = splitFullName(s.name || s.display_name);
    const first_name = firstText(s.first_name, split.first_name);
    const middle_name = firstText(s.middle_name, split.middle_name);
    const last_name = firstText(s.last_name, split.last_name);

    const nationality_id = firstId(
      s.nationality_id_int,
      s.nationality_id,
      s.country_id,
      s.nationality
    );
    const school_id = firstId(s.school_id_int, s.school_id, s.school);
    const faculty_id = firstId(s.faculty_id_int, s.faculty_id, s.faculty);
    const department_id = firstId(s.department_id_int, s.department_id, s.department);
    const batch_id = firstId(s.batch_id_int, s.batch_id, s.batch);
    const admission_intake_id = firstId(
      s.admission_intake_id_int,
      s.admission_intake_id,
      s.admission_intake,
      s.intake_id,
      s.admission_register_id
    );

    const nationalityName = firstText(
      s.nationality_name,
      s.nationality_id,
      s.nationality,
      s.country_id
    );
    const schoolName = firstText(s.school_name, s.school_id, s.school);
    const facultyName = firstText(s.faculty_name, s.faculty_id, s.faculty);
    const departmentName = firstText(s.department_name, s.department_id, s.department);
    const batchName = firstText(s.batch_name, s.batch_id, s.batch);
    const intakeName = firstText(
      s.admission_intake_name,
      s.intake_name,
      s.admission_intake_id,
      s.admission_intake,
      s.intake_id
    );

    selectedOptionsRef.current = {
      school_id,
      school_name: schoolName,
      faculty_id,
      faculty_name: facultyName,
      department_id,
      department_name: departmentName,
      batch_id,
      batch_name: batchName,
      admission_intake_id,
      admission_intake_name: intakeName,
      nationality_id,
      nationality_name: nationalityName,
    };

    // Keep dropdown labels even when lookup lists are incomplete / race later
    if (nationality_id) {
      setNationalities((prev) => upsertOption(prev, nationality_id, nationalityName));
    }
    if (school_id) setSchools((prev) => upsertOption(prev, school_id, schoolName));
    if (faculty_id) setFaculties((prev) => upsertOption(prev, faculty_id, facultyName));
    if (department_id) {
      setDepartments((prev) => upsertOption(prev, department_id, departmentName));
    }
    if (batch_id) setBatches((prev) => upsertOption(prev, batch_id, batchName));
    if (admission_intake_id) {
      setIntakes((prev) => upsertOption(prev, admission_intake_id, intakeName));
    }

    const birthRaw = firstText(s.birth_date, s.birthdate, s.date_of_birth);
    const gradRaw = firstText(s.graduation_year, s.year_of_graduation);
    const genderRaw = firstText(s.gender, s.gender_selection).toLowerCase();
    const gender =
      genderRaw === 'f' || genderRaw === 'female'
        ? 'female'
        : genderRaw === 'm' || genderRaw === 'male'
          ? 'male'
          : '';

    const discount =
      s.tuition_discount_percentage != null && s.tuition_discount_percentage !== false
        ? s.tuition_discount_percentage
        : s.discount != null && s.discount !== false
          ? s.discount
          : 0;

    const parentTypeRaw = firstText(s.parent_type, s.relationship).toLowerCase();
    const parentType = PARENT_TYPES.some((p) => p.value === parentTypeRaw) ? parentTypeRaw : '';
    const category = normalizeCategory(
      s.category ||
        s.student_category ||
        s.category_name ||
        (s.is_undergraduate === true ? 'undergraduate' : null) ||
        (s.is_postgraduate === true ? 'postgraduate' : null) ||
        s.category_id
    );

    setForm({
      ...EMPTY_FORM,
      first_name,
      middle_name,
      last_name,
      mother_name: firstText(s.mother_name, s.mother, s.parent_name),
      gender,
      birth_date: birthRaw.slice(0, 10),
      birth_place: firstText(s.birth_place, s.place_of_birth),
      nationality_id,
      phone: firstText(s.phone, s.student_phone),
      mobile: firstText(s.parent_phone, s.mobile),
      email: firstText(s.email, s.student_email, s.work_email),
      blood_group: normalizeBloodGroup(
        s.blood_group_label || s.blood_group || s.blood_group_id
      ),
      national_id: firstText(s.national_id, s.national_id_card, s.identification_id, s.visa_info),
      hemis_number: firstText(s.hemis_number, s.hemis_no, s.gr_no),
      tuition_discount_percentage: String(discount),
      school_id,
      roll_no: firstText(s.roll_no, s.roll_number, s.school_roll_no),
      certificate_number: firstText(s.certificate_number, s.certificate_no),
      graduation_year: gradRaw ? gradRaw.slice(0, 10) : '',
      average:
        s.average != null && s.average !== false
          ? String(s.average)
          : firstText(s.grade, s.cgpa),
      admission_intake_id,
      district: districtValue(firstText(s.district, s.city, s.street2), districts),
      mode: firstText(s.mode),
      campus: firstText(s.campus, s.campus_code) || '',
      faculty_id,
      department_id,
      batch_id,
      category,
      parent_type: parentType,
      parent_name: firstText(s.parent_name, s.mother_name, s.guardian_name, s.father_name),
      parent_phone: firstText(s.parent_phone, s.mobile, s.guardian_phone, s.parent_mobile),
      parent_email: firstText(s.parent_email, s.guardian_email),
      image: null,
    });
    setMeta({
      admission_number: firstText(s.admission_number, s.application_number, s.gr_no),
      student_no: firstText(s.student_no),
      state: firstText(s.state) || 'draft',
      state_label: firstText(s.state_label, s.state) || 'Registration',
      active: !(
        s.active === false ||
        s.active === 0 ||
        s.active === '0' ||
        s.active === 'false'
      ),
      department_name: departmentName,
      batch_name: batchName,
      image_url:
        s.image_url ||
        (typeof s.image === 'string' && s.image.length > 40
          ? `data:image/png;base64,${s.image}`
          : null) ||
        s.image_128 ||
        null,
      can_admit: s.can_admit === true || firstText(s.state) === 'draft',
      can_generate_student_no:
        s.can_generate_student_no === true ||
        (firstText(s.state) === 'admitted' && !firstText(s.student_no)),
    });
    setPhotoPreview(null);
    setDirty(false);
  };

  const resetNewForm = useCallback(() => {
    setForm({ ...EMPTY_FORM });
    setMeta({
      admission_number: '',
      student_no: '',
      state: 'draft',
      state_label: 'Registration',
      active: true,
      department_name: '',
      batch_name: '',
      image_url: null,
      can_admit: false,
      can_generate_student_no: false,
    });
    setPhotoPreview(null);
    setDirty(false);
    setError(null);
    setTab('family');
    setLoading(false);
  }, []);

  const loadStudent = useCallback(async () => {
    if (isNew) {
      resetNewForm();
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getStudent({ student_id: Number(studentId), include_image: true }, token)
    );
    if (fetchError === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      setLoading(false);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }
    mapStudentToForm(data.student || data.record || data);
    setLoading(false);
  }, [ensureHrToken, isNew, studentId, user?.hr_api_error, resetNewForm]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadStudent();
  }, [loadStudent]);

  useEffect(() => {
    loadDepartments(form.faculty_id);
  }, [form.faculty_id, loadDepartments]);

  useEffect(() => {
    loadBatches(form.department_id, form.faculty_id);
  }, [form.department_id, form.faculty_id, loadBatches]);

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setPhotoPreview(result);
      setField('image', base64);
    };
    reader.readAsDataURL(file);
  };

  const buildPayload = () => {
    const natId = Number(form.nationality_id);
    const resolvedNat =
      Number.isFinite(natId) && natId > 0
        ? natId
        : (() => {
            const byName = nationalities.find(
              (n) =>
                String(n.name).toLowerCase() === String(form.nationality_id || '').toLowerCase()
            );
            return byName ? Number(byName.id) : 0;
          })();

    const payload = {
      first_name: form.first_name.trim(),
      middle_name: form.middle_name.trim(),
      last_name: form.last_name.trim(),
      gender: form.gender,
      birth_date: form.birth_date,
      phone: form.phone.trim(),
      faculty_id: Number(form.faculty_id),
      department_id: Number(form.department_id),
      batch_id: Number(form.batch_id),
      school_id: Number(form.school_id),
      roll_no: form.roll_no.trim(),
      certificate_number: form.certificate_number.trim(),
      admission_intake_id: Number(form.admission_intake_id),
      campus: form.campus,
      mode: form.mode || null,
      email: form.email.trim(),
      // This portal is undergraduate admissions only
      category: 'undergraduate',
      student_category: 'undergraduate',
    };
    if (resolvedNat) {
      payload.nationality_id = resolvedNat;
    }
    const optional = [
      'mobile',
      'mother_name',
      'birth_place',
      'parent_type',
      'parent_name',
      'parent_phone',
      'parent_email',
      'blood_group',
      'national_id',
      'hemis_number',
      'district',
      'graduation_year',
      'category',
    ];
    optional.forEach((key) => {
      if (!form[key]) return;
      if (key === 'blood_group') {
        payload.blood_group = bloodGroupForApi(form.blood_group);
        return;
      }
      payload[key] = String(form[key]).trim();
    });
    if (form.average !== '') payload.average = Number(form.average);
    if (form.tuition_discount_percentage !== '') {
      payload.tuition_discount_percentage = Number(form.tuition_discount_percentage);
    }
    if (form.image) payload.image = form.image;
    return payload;
  };

  const closeActions = () => {
    setActionsOpen(false);
    setPrintOpen(false);
  };

  const onPrintIdCard = async () => {
    if (isNew || actionBusy) return;
    closeActions();
    setActionBusy(true);
    setError(null);
    setActionMessage(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.printStudentIdCard(Number(studentId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const b64 = data?.pdf_base64;
    if (!b64) {
      setError('Student ID Card PDF was empty.');
      return;
    }
    try {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: data?.mimetype || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        const a = document.createElement('a');
        a.href = url;
        a.download = data?.filename || 'Student-ID-Card.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setActionMessage('Student ID Card opened. Use the browser print dialog if needed.');
    } catch (err) {
      setError(err?.message || 'Could not open Student ID Card PDF.');
    }
  };

  const onPrintAdmissionLetter = async () => {
    if (isNew || actionBusy || meta.state !== 'admitted') return;
    closeActions();
    setActionBusy(true);
    setError(null);
    setActionMessage(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.printAdmissionLetter(Number(studentId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const b64 = data?.pdf_base64;
    if (!b64) {
      setError('Admission Letter PDF was empty.');
      return;
    }
    try {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: data?.mimetype || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data?.filename || 'Admission-Letter.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setActionMessage('Admission Letter downloaded.');
    } catch (err) {
      setError(err?.message || 'Could not download Admission Letter PDF.');
    }
  };

  const onDuplicate = async () => {
    if (isNew || actionBusy) return;
    closeActions();
    setActionBusy(true);
    setError(null);
    setActionMessage(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.duplicateStudent(Number(studentId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const newId = data?.student?.id;
    if (newId) {
      navigate(`/bu-dashboard/admissions/students/${newId}`, { replace: true });
    }
  };

  const onToggleArchive = async () => {
    if (isNew || actionBusy) return;
    closeActions();
    setArchiveOpen(true);
  };

  const onArchiveConfirm = async () => {
    if (isNew || actionBusy) return;
    setActionBusy(true);
    setError(null);
    setActionMessage(null);
    const nextActive = isArchived; // archived → restore true; active → archive false
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.archiveStudent(Number(studentId), nextActive, token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      setArchiveOpen(false);
      return;
    }
    setArchiveOpen(false);
    const student = data?.student;
    const nowActive =
      student && typeof student.active !== 'undefined'
        ? !(
            student.active === false ||
            student.active === 0 ||
            student.active === '0' ||
            student.active === 'false'
          )
        : nextActive;
    if (student) {
      mapStudentToForm({ ...student, active: nowActive });
    } else {
      setMeta((prev) => ({ ...prev, active: nowActive }));
    }
    setActionMessage(nowActive ? 'Student restored (unarchived).' : 'Student archived.');
  };

  const openDeleteModal = () => {
    if (isNew || actionBusy) return;
    closeActions();
    setDeleteOpen(true);
  };

  const onDeleteConfirm = async () => {
    if (isNew || actionBusy) return;
    setActionBusy(true);
    setError(null);
    const { error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.deleteStudent(Number(studentId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      setDeleteOpen(false);
      return;
    }
    setDeleteOpen(false);
    navigate('/bu-dashboard/admissions/students', { replace: true });
  };

  const onTransferStudent = () => {
    if (isNew || actionBusy) return;
    closeActions();
    setTransferOpen(true);
  };

  const onTransferSuccess = (data) => {
    const student = data?.student;
    if (student) {
      mapStudentToForm(student);
    } else {
      loadStudent();
    }
    setActionMessage(
      data?.message ||
        (data?.new_student_no
          ? `Student transferred successfully. New Student ID: ${data.new_student_no}`
          : 'Student transferred successfully.')
    );
  };

  const onExternalTransfer = () => {
    if (isNew || actionBusy) return;
    closeActions();
    setExternalTransferOpen(true);
  };

  const onExternalTransferSuccess = (data) => {
    const student = data?.student;
    if (student) mapStudentToForm(student);
    else loadStudent();
    setActionMessage(
      data?.message ||
        (data?.new_student_no
          ? `External transfer approved. BU Student ID: ${data.new_student_no}`
          : 'External transfer approved.')
    );
    if (!data?.pdf_base64) return;
    try {
      const binary = atob(data.pdf_base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: data?.mimetype || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data?.filename || 'Admission-Letter.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (downloadError) {
      setError(downloadError?.message || 'External transfer succeeded, but the Admission Letter could not be downloaded.');
    }
  };

  const onSave = async (e, options = {}) => {
    e?.preventDefault?.();
    const skipNavigate = Boolean(options.skipNavigate);
    setSaving(true);
    setError(null);
    const payload = buildPayload();
    if (!payload.nationality_id) {
      setError('Please select a nationality from the list (type Somalia, then click or press Enter).');
      setSaving(false);
      return null;
    }
    if (!payload.first_name || !payload.middle_name || !payload.last_name) {
      setError('First name, middle name and last name are required.');
      setSaving(false);
      return null;
    }
    if (!payload.email) {
      setError('Student email is required.');
      setSaving(false);
      return null;
    }
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      isNew
        ? admissionsAPI.createStudent(payload, token)
        : admissionsAPI.updateStudent({ student_id: Number(studentId), ...payload }, token)
    );
    if (fetchError === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      setSaving(false);
      return null;
    }
    if (fetchError) {
      setError(fetchError);
      setSaving(false);
      return null;
    }
    const createdId = data?.student?.id || data?.id || data?.student_id || Number(studentId);
    if (data?.student) {
      mapStudentToForm(data.student);
    }
    setDirty(false);
    setSaving(false);
    if (isNew && createdId && !skipNavigate) {
      setActionMessage(data?.message || 'Student registration saved.');
      navigate(`/bu-dashboard/admissions/students/${createdId}`, { replace: true });
    } else if (!data?.student && !skipNavigate) {
      loadStudent();
    } else if (data?.message && !skipNavigate) {
      setActionMessage(data.message);
    }
    return createdId || true;
  };

  const canAdmit = isNew || meta.state === 'draft' || meta.state === '' || meta.can_admit;
  const alreadyAdmitted = meta.state === 'admitted' && Boolean(meta.student_no);
  const showAdmitButton = canAdmit && !alreadyAdmitted;
  const canGenerateMissingId =
    !isNew &&
    (meta.can_generate_student_no || (meta.state === 'admitted' && !meta.student_no));
  const canPrintAdmissionLetter = !isNew && meta.state === 'admitted';

  const openAdmitModal = () => {
    if (actionBusy || saving || alreadyAdmitted) return;
    setError(null);
    setAdmitOpen(true);
  };

  const onAdmitConfirm = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    setError(null);
    let id = Number(studentId);
    if (isNew || dirty) {
      const savedId = await onSave(undefined, { skipNavigate: true });
      if (!savedId) {
        setActionBusy(false);
        return;
      }
      id = Number(savedId) || id;
    }
    if (!id) {
      setActionBusy(false);
      setError('Save the registration first, then confirm admission.');
      return;
    }
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.admitStudent(id, token)
    );
    setActionBusy(false);
    if (fetchError === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      return;
    }
    setAdmitOpen(false);
    if (data?.student) {
      mapStudentToForm(data.student);
    } else {
      loadStudent();
    }
    setActionMessage(
      data?.message ||
        `Registration confirmed. Student admitted with permanent Student ID: ${
          data?.student?.student_no || ''
        }`
    );
    if (isNew && id) {
      navigate(`/bu-dashboard/admissions/students/${id}`, { replace: true });
    }
  };

  const onDiscard = () => {
    if (isNew || dirty) {
      navigate('/bu-dashboard/admissions/students');
      return;
    }
    loadStudent();
  };

  if (loading) {
    return (
      <div className="ea-odoo-form-page ea-adm-form">
        <div className="ea-odoo-empty">Loading student…</div>
      </div>
    );
  }

  return (
    <div className="ea-odoo-form-page ea-adm-form">
      {/*
        Odoo control panel states:
        1) Open student (clean) → New + stacked title + gear + bookmark/pager
        2) Open student + edits → + cloud Save + X Discard
        3) New → New + Student Admissions + gear/cloud/X + bookmark/views
      */}
      <div className="ea-odoo-form-bar ea-adm-cp">
        <div className="ea-adm-cp__left">
          <button
            type="button"
            className="ea-odoo-btn ea-adm-cp__new"
            onClick={() => {
              // Force a blank registration form even when leaving an open student
              navigate('/bu-dashboard/admissions/students/new');
              if (isNew) resetNewForm();
            }}
          >
            New
          </button>

          <div className="ea-adm-cp__title-block">
            <Link
              to="/bu-dashboard/admissions/students"
              className="ea-adm-cp__parent"
            >
              Students Admission
            </Link>
            {!isNew && (
              <div className="ea-adm-cp__record-row">
                <span className="ea-adm-cp__record">{titleName}</span>
                <div className="ea-adm-actions" ref={actionsRef}>
                  <button
                    type="button"
                    className="ea-adm-cp__icon"
                    title="Actions"
                    aria-label="Actions"
                    aria-expanded={actionsOpen}
                    aria-haspopup="menu"
                    onClick={() => setActionsOpen((v) => !v)}
                    disabled={actionBusy}
                  >
                    <MdSettings size={16} />
                  </button>
                  {actionsOpen ? (
                    <div className="ea-adm-actions__menu ea-adm-actions__menu--wide" role="menu">
                      <div
                        className={`ea-adm-actions__item ea-adm-actions__item--has-sub${
                          printOpen ? ' is-open' : ''
                        }`}
                        role="none"
                        onMouseEnter={() => setPrintOpen(true)}
                        onFocus={() => setPrintOpen(true)}
                      >
                        <button
                          type="button"
                          className="ea-adm-actions__item-btn"
                          role="menuitem"
                          aria-haspopup="menu"
                          aria-expanded={printOpen}
                          onClick={() => setPrintOpen((v) => !v)}
                          disabled={actionBusy}
                        >
                          <span className="ea-adm-actions__item-main">
                            <MdPrint size={16} aria-hidden />
                            Print
                          </span>
                          <MdChevronRight size={16} aria-hidden />
                        </button>
                        {printOpen ? (
                          <div className="ea-adm-actions__submenu" role="menu">
                            <button
                              type="button"
                              className="ea-adm-actions__item"
                              role="menuitem"
                              onClick={onPrintIdCard}
                              disabled={actionBusy}
                            >
                              Student ID Card
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="ea-adm-actions__item"
                        role="menuitem"
                        onClick={onDuplicate}
                        disabled={actionBusy}
                      >
                        <MdContentCopy size={16} aria-hidden />
                        Duplicate
                      </button>
                      <button
                        type="button"
                        className="ea-adm-actions__item"
                        role="menuitem"
                        onClick={onToggleArchive}
                        disabled={actionBusy}
                      >
                        {isArchived ? (
                          <MdUnarchive size={16} aria-hidden />
                        ) : (
                          <MdArchive size={16} aria-hidden />
                        )}
                        {isArchived ? 'Unarchive' : 'Archive'}
                      </button>
                      <button
                        type="button"
                        className="ea-adm-actions__item ea-adm-actions__item--danger"
                        role="menuitem"
                        onClick={openDeleteModal}
                        disabled={actionBusy}
                      >
                        <MdDeleteOutline size={16} aria-hidden />
                        Delete
                      </button>
                      <div className="ea-adm-actions__sep" role="separator" />
                      <button
                        type="button"
                        className="ea-adm-actions__item"
                        role="menuitem"
                        onClick={onTransferStudent}
                      >
                        <MdSwapHoriz size={16} aria-hidden />
                        Transfer Student
                      </button>
                      <button
                        type="button"
                        className="ea-adm-actions__item"
                        role="menuitem"
                        onClick={onExternalTransfer}
                        disabled={actionBusy}
                      >
                        <MdPublic size={16} aria-hidden />
                        Process External Transfer
                      </button>
                    </div>
                  ) : null}
                </div>
                {showDirtyActions && (
                  <>
                    <button
                      type="button"
                      className="ea-adm-cp__icon ea-adm-cp__icon--save"
                      onClick={onSave}
                      disabled={saving || actionBusy}
                      title="Save"
                      aria-label="Save"
                    >
                      <MdCloudUpload size={18} />
                    </button>
                    <button
                      type="button"
                      className="ea-adm-cp__icon"
                      onClick={onDiscard}
                      disabled={saving || actionBusy}
                      title="Discard"
                      aria-label="Discard"
                    >
                      <MdClose size={18} />
                    </button>
                  </>
                )}
              </div>
            )}
            {isNew && (
              <div className="ea-adm-cp__record-row">
                <button
                  type="button"
                  className="ea-adm-cp__icon"
                  title="Actions"
                  aria-label="Actions"
                  disabled
                >
                  <MdSettings size={16} />
                </button>
                <button
                  type="button"
                  className="ea-adm-cp__icon ea-adm-cp__icon--save"
                  onClick={onSave}
                  disabled={saving}
                  title="Save"
                  aria-label="Save"
                >
                  <MdCloudUpload size={18} />
                </button>
                <button
                  type="button"
                  className="ea-adm-cp__icon"
                  onClick={onDiscard}
                  disabled={saving}
                  title="Discard"
                  aria-label="Discard"
                >
                  <MdClose size={18} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="ea-adm-cp__right">
          <button
            type="button"
            className="ea-adm-cp__icon"
            title="Favorite"
            aria-label="Favorite"
          >
            <MdBookmarkBorder size={18} />
          </button>
          {isNew ? (
            <button
              type="button"
              className="ea-adm-cp__icon"
              title="Views"
              aria-label="Views"
              onClick={() => navigate('/bu-dashboard/admissions/students')}
            >
              <MdViewModule size={18} />
            </button>
          ) : (
            <div className="ea-odoo-pager ea-adm-cp__pager" aria-label="Pagination">
              <span>1 / 1</span>
              <div className="ea-adm-cp__pager-btns">
                <button
                  type="button"
                  className="ea-odoo-pager__btn"
                  onClick={() => navigate('/bu-dashboard/admissions/students')}
                  aria-label="Previous"
                >
                  <MdChevronLeft size={18} />
                </button>
                <button type="button" className="ea-odoo-pager__btn" disabled aria-label="Next">
                  <MdChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      )}
      {actionMessage && (
        <div className="ea-odoo-alert ea-odoo-alert--info" role="status">
          {actionMessage}
        </div>
      )}

      <form className="ea-odoo-sheet" onSubmit={onSave}>
        <div className="ea-adm-sheet__toolbar">
          <div className="ea-adm-sheet__toolbar-left">
            {canPrintAdmissionLetter ? (
              <button
                type="button"
                className="ea-odoo-btn"
                onClick={onPrintAdmissionLetter}
                disabled={actionBusy}
              >
                {actionBusy ? 'Preparing Letter…' : 'Print Admission Letter'}
              </button>
            ) : null}
            {showAdmitButton ? (
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--primary"
                onClick={openAdmitModal}
                disabled={saving || actionBusy}
              >
                Confirm Registration &amp; Admit
              </button>
            ) : null}
            {canGenerateMissingId ? (
              <button
                type="button"
                className="ea-odoo-btn"
                onClick={openAdmitModal}
                disabled={saving || actionBusy}
              >
                Generate Missing Student ID
              </button>
            ) : null}
          </div>
          <div className="ea-adm-sheet__statusbar" role="status" aria-label="Admission status">
            {STATUS_STEPS.map((st) => (
              <span
                key={st.value}
                className={`ea-adm-sheet__status${
                  (meta.state || 'draft') === st.value ? ' is-active' : ''
                }`}
              >
                {st.label}
              </span>
            ))}
          </div>
        </div>

        <header className="ea-odoo-sheet__header ea-adm-sheet__header">
          <button
            type="button"
            className="ea-odoo-sheet__photo"
            onClick={() => fileInputRef.current?.click()}
            aria-label={photoSrc ? 'Change photo' : 'Add photo'}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="ea-odoo-sheet__photo-input"
              onChange={handleImagePick}
              tabIndex={-1}
            />
            {photoSrc ? (
              <img src={photoSrc} alt="" />
            ) : (
              <span className="ea-odoo-sheet__photo-placeholder">
                <MdAdd size={28} />
                <span>{getInitials(fullName || 'ST')}</span>
              </span>
            )}
            <span className="ea-odoo-sheet__photo-overlay">
              <MdPhotoCamera size={22} />
              <span>{photoSrc ? 'Edit' : 'Upload'}</span>
            </span>
          </button>

          <div className="ea-adm-sheet__identity">
            <div className="ea-adm-sheet__admno">
              Admission No : {meta.admission_number || (isNew ? '(auto)' : '—')}
              {meta.student_no ? (
                <span className="ea-adm-sheet__student-no"> {meta.student_no}</span>
              ) : null}
            </div>
            <div className="ea-adm-sheet__placement" aria-label="Faculty, department and batch">
              <label className="ea-adm-sheet__place-field">
                <span className="ea-adm-sheet__place-label">
                  Faculty<span className="ea-adm-req">*</span>
                </span>
                <OdooAutocomplete
                  className="ea-odoo-ac--faculty"
                  value={form.faculty_id}
                  options={faculties}
                  required
                  aria-label="Faculty"
                  onChange={(id, opt) => {
                    setField('faculty_id', id);
                    setField('department_id', '');
                    setField('batch_id', '');
                    selectedOptionsRef.current = {
                      ...selectedOptionsRef.current,
                      faculty_id: id,
                      faculty_name: opt?.name || opt?.label || '',
                      department_id: '',
                      department_name: '',
                      batch_id: '',
                      batch_name: '',
                    };
                  }}
                />
              </label>
              <label className="ea-adm-sheet__place-field">
                <span className="ea-adm-sheet__place-label">
                  Department<span className="ea-adm-req">*</span>
                </span>
                <OdooAutocomplete
                  className="ea-odoo-ac--dept"
                  value={form.department_id}
                  options={departments}
                  required
                  aria-label="Department"
                  onChange={(id, opt) => {
                    setField('department_id', id);
                    setField('batch_id', '');
                    selectedOptionsRef.current = {
                      ...selectedOptionsRef.current,
                      department_id: id,
                      department_name: opt?.name || opt?.label || '',
                      batch_id: '',
                      batch_name: '',
                    };
                  }}
                />
              </label>
              <label className="ea-adm-sheet__place-field">
                <span className="ea-adm-sheet__place-label">
                  Batch<span className="ea-adm-req">*</span>
                </span>
                <OdooAutocomplete
                  className="ea-odoo-ac--batch"
                  value={form.batch_id}
                  options={batches}
                  required
                  aria-label="Batch"
                  onChange={(id, opt) => {
                    setField('batch_id', id);
                    selectedOptionsRef.current = {
                      ...selectedOptionsRef.current,
                      batch_id: id,
                      batch_name: opt?.name || opt?.label || '',
                    };
                  }}
                />
              </label>
            </div>
          </div>
        </header>

        <div className="ea-odoo-sheet__body">
          <div className="ea-odoo-cols">
            <Group title="Personal Information">
              <Field label="Full Name">
                <input value={fullName} readOnly placeholder="Built from names below" />
              </Field>
              <Field label="First Name" required>
                <input
                  value={form.first_name}
                  onChange={(e) => setField('first_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Middle Name" required>
                <input
                  value={form.middle_name}
                  onChange={(e) => setField('middle_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Last Name" required>
                <input
                  value={form.last_name}
                  onChange={(e) => setField('last_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Mother Name">
                <input
                  value={form.mother_name}
                  onChange={(e) => setField('mother_name', e.target.value)}
                />
              </Field>
              <Field label="Gender" required>
                <RadioRow
                  name="gender"
                  value={form.gender}
                  onChange={(v) => setField('gender', v)}
                  required
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                  ]}
                />
              </Field>
              <Field label="Date of birth" required>
                <input
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => setField('birth_date', e.target.value)}
                  required
                />
              </Field>
              <Field label="Birth Place">
                <input
                  value={form.birth_place}
                  onChange={(e) => setField('birth_place', e.target.value)}
                />
              </Field>
              <Field label="Nationality" required>
                <OdooAutocomplete
                  options={nationalities}
                  value={form.nationality_id}
                  onChange={(id) => setField('nationality_id', id)}
                  onSearch={searchNationalities}
                  required
                  placeholder="Type to search country"
                  aria-label="Nationality"
                  emptyText="No countries match"
                />
              </Field>
            </Group>

            <Group title="Contact Details">
              <Field label="Student Phone Number" required>
                <input
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  required
                />
              </Field>
              <Field label="Student Email" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  required
                />
              </Field>
              <Field label="Blood Group">
                <OdooAutocomplete
                  value={form.blood_group}
                  options={BLOOD_GROUPS}
                  onChange={(id) => setField('blood_group', id)}
                  aria-label="Blood Group"
                />
              </Field>
              <Field label="National ID Card">
                <input
                  value={form.national_id}
                  onChange={(e) => setField('national_id', e.target.value)}
                />
              </Field>
              <Field label="Hemis Number">
                <input
                  value={form.hemis_number}
                  onChange={(e) => setField('hemis_number', e.target.value)}
                />
              </Field>
            </Group>
          </div>

          <Group title="Fee Discount" wide>
            <Field label="Tuition Discount (%)" help="Percentage discount on tuition fees">
              <div className="ea-adm-percent">
                <input
                  value={form.tuition_discount_percentage}
                  onChange={(e) => setField('tuition_discount_percentage', e.target.value)}
                />
                <span>%</span>
              </div>
            </Field>
          </Group>

          <section className="ea-odoo-group ea-odoo-group--wide">
            <h3 className="ea-odoo-group__title">Education Information</h3>
            <div className="ea-odoo-cols">
              <div className="ea-odoo-group__body">
                <Field label="School Name" required>
                  <OdooAutocomplete
                    value={form.school_id}
                    options={schools}
                    onChange={(id) => setField('school_id', id)}
                    onSearch={searchSchools}
                    onCreate={createSchool}
                    creating={schoolCreating}
                    required
                    aria-label="School Name"
                  />
                </Field>
                <Field label="Roll Name School" required>
                  <input
                    value={form.roll_no}
                    onChange={(e) => setField('roll_no', e.target.value)}
                    required
                  />
                </Field>
                <Field label="Certificate Number" required>
                  <input
                    value={form.certificate_number}
                    onChange={(e) => setField('certificate_number', e.target.value)}
                    required
                  />
                </Field>
                <Field label="Year of graduation">
                  <input
                    type="date"
                    value={form.graduation_year}
                    onChange={(e) => setField('graduation_year', e.target.value)}
                  />
                </Field>
                <Field label="Average">
                  <input
                    value={form.average}
                    onChange={(e) => setField('average', e.target.value)}
                  />
                </Field>
              </div>
              <div className="ea-odoo-group__body">
                <Field label="Admission Intake" required>
                  <OdooAutocomplete
                    value={form.admission_intake_id}
                    options={intakes}
                    onChange={(id) => setField('admission_intake_id', id)}
                    required
                    aria-label="Admission Intake"
                  />
                </Field>
                <Field label="District">
                  <OdooAutocomplete
                    value={form.district}
                    options={districts}
                    onChange={(id) => setField('district', id)}
                    aria-label="District"
                  />
                </Field>
                <Field label="Mode">
                  <select
                    value={form.mode}
                    onChange={(e) => setField('mode', e.target.value)}
                    aria-label="Mode"
                  >
                    <option value="">Select mode</option>
                    <option value="evening">Evening</option>
                    <option value="part_time">Part Time</option>
                    <option value="full_time">Full Time</option>
                    <option value="online">Online</option>
                  </select>
                </Field>
                <Field label="Campus" required>
                  <OdooAutocomplete
                    value={form.campus}
                    options={campuses}
                    onChange={(id) => setField('campus', id)}
                    required
                    aria-label="Campus"
                  />
                </Field>
                <Field label="Batch" required>
                  <OdooAutocomplete
                    value={form.batch_id}
                    options={batches}
                    required
                    aria-label="Batch"
                    onChange={(id, opt) => {
                      setField('batch_id', id);
                      selectedOptionsRef.current = {
                        ...selectedOptionsRef.current,
                        batch_id: id,
                        batch_name: opt?.name || opt?.label || '',
                      };
                    }}
                  />
                </Field>
                <Field label="Student Category" help="Undergraduate category">
                  <RadioRow
                    name="category"
                    value={form.category}
                    onChange={(v) => setField('category', v)}
                    options={[{ value: 'undergraduate', label: 'Undergraduate' }]}
                  />
                </Field>
              </div>
            </div>
          </section>
        </div>

        <div className="ea-odoo-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`ea-odoo-tabs__btn${tab === 'family' ? ' active' : ''}`}
            aria-selected={tab === 'family'}
            onClick={() => setTab('family')}
          >
            Family Information
          </button>
          <button
            type="button"
            role="tab"
            className={`ea-odoo-tabs__btn${tab === 'accounting' ? ' active' : ''}`}
            aria-selected={tab === 'accounting'}
            onClick={() => setTab('accounting')}
          >
            Accounting
          </button>
        </div>

        <div className="ea-odoo-sheet__body ea-adm-tab-body">
          {tab === 'family' ? (
            <Group title="Family Information" wide>
              <Field label="Parent Type">
                <RadioRow
                  name="parent_type"
                  value={form.parent_type}
                  onChange={(v) => setField('parent_type', v)}
                  options={PARENT_TYPES}
                />
              </Field>
              <Field label="Parent Name">
                <input
                  value={form.parent_name}
                  onChange={(e) => setField('parent_name', e.target.value)}
                />
              </Field>
              <Field label="Parent Phone Number">
                <input
                  value={form.parent_phone}
                  onChange={(e) => setField('parent_phone', e.target.value)}
                />
              </Field>
              <Field label="Parent Email">
                <input
                  type="email"
                  value={form.parent_email}
                  onChange={(e) => setField('parent_email', e.target.value)}
                />
              </Field>
            </Group>
          ) : (
            <p className="ea-odoo-muted">
              Accounting details are managed in Odoo. This tab is read-only in the portal for now.
            </p>
          )}
        </div>

        <div className="ea-adm-form-actions">
          <button type="submit" className="ea-odoo-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="ea-odoo-btn ea-odoo-btn--primary"
            disabled
            title="Coming soon"
          >
            Send Welcome Email
          </button>
        </div>
      </form>

      {deleteOpen ? (
        <div
          className="ea-odoo-modal-backdrop"
          role="presentation"
          onClick={() => !actionBusy && setDeleteOpen(false)}
        >
          <div
            className="ea-odoo-modal ea-odoo-modal--delete"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-student-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-student-delete-title">Bye-bye, record!</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={actionBusy}
                onClick={() => setDeleteOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>Ready to make your record disappear into thin air? Are you sure?</p>
              <p>It will be gone forever!</p>
              <p>Think twice before you click that &apos;Delete&apos; button!</p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={actionBusy}
                onClick={onDeleteConfirm}
              >
                {actionBusy ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={actionBusy}
                onClick={() => setDeleteOpen(false)}
              >
                No, keep it
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {admitOpen ? (
        <div
          className="ea-odoo-modal-backdrop"
          role="presentation"
          onClick={() => !actionBusy && setAdmitOpen(false)}
        >
          <div
            className="ea-odoo-modal ea-odoo-modal--confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-student-admit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-student-admit-title">Confirmation</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={actionBusy}
                onClick={() => setAdmitOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>
                {canGenerateMissingId
                  ? 'This will generate a permanent Student ID for this previously admitted record. Continue?'
                  : 'This will generate the permanent Student ID and admit the student. Continue?'}
              </p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={actionBusy}
                onClick={onAdmitConfirm}
              >
                {actionBusy ? 'Admitting…' : 'Ok'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={actionBusy}
                onClick={() => setAdmitOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {archiveOpen ? (
        <div
          className="ea-odoo-modal-backdrop"
          role="presentation"
          onClick={() => !actionBusy && setArchiveOpen(false)}
        >
          <div
            className="ea-odoo-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-student-archive-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-student-archive-title">Confirmation</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={actionBusy}
                onClick={() => setArchiveOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>
                {isArchived
                  ? 'Are you sure that you want to unarchive this record?'
                  : 'Are you sure that you want to archive this record?'}
              </p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={actionBusy}
                onClick={onArchiveConfirm}
              >
                {actionBusy
                  ? isArchived
                    ? 'Unarchiving…'
                    : 'Archiving…'
                  : isArchived
                    ? 'Unarchive'
                    : 'Archive'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={actionBusy}
                onClick={() => setArchiveOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <TransferStudentModal
        open={transferOpen}
        studentId={studentId}
        ensureHrToken={ensureHrToken}
        onClose={() => setTransferOpen(false)}
        onSuccess={onTransferSuccess}
      />
      <ExternalTransferModal
        open={externalTransferOpen}
        studentId={studentId}
        ensureHrToken={ensureHrToken}
        onClose={() => setExternalTransferOpen(false)}
        onSuccess={onExternalTransferSuccess}
      />
    </div>
  );
}
